import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSortBy, useTable } from "react-table";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";
import { useAccountsIsolatedHealth } from "../../hooks/api/admin/useAccountsIsolatedHealth";
import {
  IGlobalIsolatedHealthPosition,
  IsolatedRiskStatus,
} from "../../interfaces/aevo";
import { compareDecimalStrings, formatDecimalString } from "../../utils/decimal";
import { ISelectOption, Select } from "../shared/Select";
import { FormInput } from "../Settings/style";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../Table/TableColumn";
import {
  Align,
  LeftTitleWrapper,
  RightTitleWrapper,
  TableHeaderCell,
  TableLoader,
  TableWrapper,
  TitleWrapper,
} from "../Table/style";

interface IStatusStyle {
  text: string;
  background: string;
}

const STATUS_STYLES: Record<IsolatedRiskStatus, IStatusStyle> = {
  liquidating: {
    text: "#F97316",
    background: "rgba(249, 115, 22, 0.18)",
  },
  breached: {
    text: COLORS.red.one,
    background: COLORS.red.four,
  },
  warning: {
    text: "#FACC15",
    background: "rgba(250, 204, 21, 0.16)",
  },
  healthy: {
    text: COLORS.green.one,
    background: COLORS.green.four,
  },
};

const STATUS_RANK: Record<IsolatedRiskStatus, number> = {
  liquidating: 0,
  breached: 1,
  warning: 2,
  healthy: 3,
};

const STATUS_LABELS: Record<IsolatedRiskStatus, string> = {
  liquidating: "LIQUIDATING",
  breached: "BREACHED",
  warning: "WARNING",
  healthy: "HEALTHY",
};

const getStatusRank = (status: unknown): number => {
  if (
    typeof status === "string" &&
    Object.prototype.hasOwnProperty.call(STATUS_RANK, status)
  ) {
    return STATUS_RANK[status as IsolatedRiskStatus];
  }

  return STATUS_RANK.healthy;
};

const decimalSortType = (rowA: any, rowB: any, columnId: string) =>
  compareDecimalStrings(rowA.values[columnId], rowB.values[columnId]);

const formatUsd = (
  value: string,
  maxFractionDigits: number = 2,
  fallback: string = "-"
) => {
  if (!value || value === "-") {
    return fallback;
  }

  const formatted = formatDecimalString(value, {
    maxFractionDigits,
    minFractionDigits: 0,
  });

  return formatted === "-" ? fallback : `$${formatted}`;
};

const formatNumber = (
  value: string,
  maxFractionDigits: number = 4,
  fallback: string = "-"
) =>
  formatDecimalString(value, {
    maxFractionDigits,
    minFractionDigits: 0,
    fallback,
  });

const getPositionSide = (position: string): "LONG" | "SHORT" | "-" => {
  const direction = compareDecimalStrings(position, "0");
  if (direction > 0) {
    return "LONG";
  }
  if (direction < 0) {
    return "SHORT";
  }
  return "-";
};

const getPositionSideColor = (side: "LONG" | "SHORT" | "-") => {
  if (side === "LONG") {
    return COLORS.green.one;
  }
  if (side === "SHORT") {
    return COLORS.red.one;
  }
  return TEXT_COLORS.two;
};

const getPnLColor = (value: string) => {
  const pnlDirection = compareDecimalStrings(value, "0");
  if (pnlDirection > 0) {
    return COLORS.green.one;
  }
  if (pnlDirection < 0) {
    return COLORS.red.one;
  }
  return TEXT_COLORS.one;
};

const summaryCardStyle: React.CSSProperties = {
  border: `1px solid ${BORDER_COLORS.two}`,
  borderRadius: 6,
  background: BACKGROUND_COLORS.four,
  padding: SPACING.two,
};

interface IIsolatedRiskMonitorProps {
  isMobile?: boolean;
}

export function IsolatedRiskMonitor({ isMobile }: IIsolatedRiskMonitorProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<IsolatedRiskStatus | "all">(
    "all"
  );
  const [accountFilter, setAccountFilter] = useState("");
  const [instrumentFilter, setInstrumentFilter] = useState("");

  const {
    data: isolatedHealth,
    isLoading,
    isValidating,
  } = useAccountsIsolatedHealth();

  const summary = isolatedHealth?.summary;
  const positions = useMemo(
    () => isolatedHealth?.positions || [],
    [isolatedHealth?.positions]
  );
  const isTableLoading = isLoading || (!isLoading && isValidating);

  const summaryCards = useMemo(
    () => [
      {
        title: "Isolated Positions",
        value: formatDecimalString(summary?.isolated_positions_count, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
        color: TEXT_COLORS.one,
      },
      {
        title: "Warning Positions",
        value: formatDecimalString(summary?.warning_positions_count, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
        color: STATUS_STYLES.warning.text,
      },
      {
        title: "Breached Positions",
        value: formatDecimalString(summary?.breached_positions_count, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
        color: STATUS_STYLES.breached.text,
      },
      {
        title: "Liquidating Positions",
        value: formatDecimalString(summary?.liquidating_positions_count, {
          maxFractionDigits: 0,
          fallback: "0",
        }),
        color: STATUS_STYLES.liquidating.text,
      },
    ],
    [
      summary?.breached_positions_count,
      summary?.isolated_positions_count,
      summary?.liquidating_positions_count,
      summary?.warning_positions_count,
    ]
  );

  const statusOptions: ISelectOption[] = useMemo(
    () => [
      {
        label: "All",
        isActive: statusFilter === "all",
        onClick: () => setStatusFilter("all"),
      },
      {
        label: "Liquidating",
        isActive: statusFilter === "liquidating",
        onClick: () => setStatusFilter("liquidating"),
      },
      {
        label: "Breached",
        isActive: statusFilter === "breached",
        onClick: () => setStatusFilter("breached"),
      },
      {
        label: "Warning",
        isActive: statusFilter === "warning",
        onClick: () => setStatusFilter("warning"),
      },
      {
        label: "Healthy",
        isActive: statusFilter === "healthy",
        onClick: () => setStatusFilter("healthy"),
      },
    ],
    [statusFilter]
  );

  const filteredPositions = useMemo(() => {
    const accountSearch = accountFilter.trim().toLowerCase();
    const instrumentSearch = instrumentFilter.trim().toLowerCase();

    return positions.filter((position) => {
      if (statusFilter !== "all" && position.risk_status !== statusFilter) {
        return false;
      }

      if (
        accountSearch &&
        !position.account.toLowerCase().includes(accountSearch)
      ) {
        return false;
      }

      if (!instrumentSearch) {
        return true;
      }

      const assetMatches = position.asset.toLowerCase().includes(instrumentSearch);
      const instrumentMatches = position.instrument_name
        .toLowerCase()
        .includes(instrumentSearch);

      return assetMatches || instrumentMatches;
    });
  }, [accountFilter, instrumentFilter, positions, statusFilter]);

  const onRowClick = useCallback(
    (account: string) => {
      navigate(`/account/${account}/isolated-health`);
    },
    [navigate]
  );

  const columns: ITableColumn<IGlobalIsolatedHealthPosition>[] = useMemo(
    () => [
      {
        title: "Status",
        accessor: "risk_status",
        align: "left",
        sortType: (rowA: any, rowB: any, columnId: string) =>
          getStatusRank(rowA.values[columnId]) -
          getStatusRank(rowB.values[columnId]),
        Cell: ({ value }) => {
          const status = value as IsolatedRiskStatus;
          const style = STATUS_STYLES[status];

          return (
            <span
              style={{
                color: style.text,
                backgroundColor: style.background,
                borderRadius: 6,
                padding: "4px 8px",
                fontSize: 11,
                letterSpacing: "0.03em",
              }}
            >
              {STATUS_LABELS[status]}
            </span>
          );
        },
      },
      {
        title: "Account",
        accessor: "account",
        align: "left",
        Cell: ({ value }) => (
          <span style={{ fontFamily: "monospace", color: TEXT_COLORS.one }}>
            {value}
          </span>
        ),
      },
      {
        title: "Instrument",
        accessor: "instrument_name",
        align: "left",
      },
      {
        title: "Asset",
        accessor: "asset",
        align: "left",
      },
      {
        title: "Side",
        id: "position_side",
        accessor: "position",
        align: "left",
        Cell: ({ value }) => {
          const side = getPositionSide(value);
          const color = getPositionSideColor(side);

          return <span style={{ color }}>{side}</span>;
        },
      },
      {
        title: "Position",
        accessor: "position",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatNumber(value),
      },
      {
        title: "Entry Price",
        accessor: "entry_price",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 6),
      },
      {
        title: "Mark Price",
        accessor: "mark_price",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 6),
      },
      {
        title: "Allocated Margin",
        accessor: "allocated_isolated_margin",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 4),
      },
      {
        title: "Base Margin",
        accessor: "base_isolated_margin",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 4),
      },
      {
        title: "UPNL",
        accessor: "upnl",
        align: "right",
        sortType: decimalSortType,
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getPnLColor(value) }}>
            {formatUsd(value, 4)}
          </Align>
        ),
      },
      {
        title: "MM",
        accessor: "maintenance_margin",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) => formatUsd(value, 4),
      },
      {
        title: "Liq Price",
        accessor: "liquidation_price",
        align: "right",
        sortType: decimalSortType,
        valueExtractor: (value) =>
          value && value !== "-" ? formatUsd(value, 6) : "-",
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: filteredPositions,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <div
      style={{
        height: `calc(100vh - ${COMPONENTS.header.main}px)`,
        padding: isMobile ? SPACING.two : SPACING.three,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          marginBottom: SPACING.three,
          color: TEXT_COLORS.two,
          textTransform: "uppercase",
          fontSize: 12,
          letterSpacing: "0.03em",
        }}
      >
        Isolated Risk Monitor
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ?
            "repeat(2, minmax(0, 1fr))" :
            "repeat(4, minmax(0, 1fr))",
          gap: SPACING.two,
          marginBottom: SPACING.three,
        }}
      >
        {summaryCards.map((summaryCard) => (
          <div key={summaryCard.title} style={summaryCardStyle}>
            <div
              style={{
                color: TEXT_COLORS.two,
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: "0.03em",
              }}
            >
              {summaryCard.title}
            </div>
            <div
              style={{
                marginTop: SPACING.one,
                color: summaryCard.color,
                fontSize: 24,
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {summaryCard.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: SPACING.two,
          flexWrap: "wrap",
          marginBottom: SPACING.two,
        }}
      >
        <div style={{ maxWidth: "100%", overflowX: "auto" }}>
          <Select options={statusOptions} />
        </div>
        <FormInput
          value={accountFilter}
          onChange={(event) => setAccountFilter(event.target.value)}
          placeholder="Filter account"
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: isMobile ? 240 : undefined,
            height: COMPONENTS.selectHeight,
          }}
        />
        <FormInput
          value={instrumentFilter}
          onChange={(event) => setInstrumentFilter(event.target.value)}
          placeholder="Filter asset / instrument"
          style={{
            width: isMobile ? "100%" : 260,
            minWidth: isMobile ? 240 : undefined,
            height: COMPONENTS.selectHeight,
          }}
        />
      </div>

      <div>
        <TitleWrapper>
          <LeftTitleWrapper>
            <h3>
              Isolated Risk Positions{" "}
              {filteredPositions.length ? `(${filteredPositions.length})` : "(None)"}
            </h3>
          </LeftTitleWrapper>
          <RightTitleWrapper>
            <span style={{ color: TEXT_COLORS.three, fontSize: 11 }}>
              Click row to open account isolated health
            </span>
          </RightTitleWrapper>
        </TitleWrapper>
        <TableWrapper>
          <TableLoader isLoading={isTableLoading} />
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                // eslint-disable-next-line react/jsx-key
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    // eslint-disable-next-line react/jsx-key
                    <TableHeaderCell
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={{ width: column.width }}
                    >
                      {column.render("Header")}
                    </TableHeaderCell>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                const { ...rowProps } = row.getRowProps();

                return (
                  // eslint-disable-next-line react/jsx-key
                  <tr
                    {...rowProps}
                    onClick={() => onRowClick(row.original.account)}
                    style={{ cursor: "pointer" }}
                  >
                    {row.cells.map((cell) => (
                      // eslint-disable-next-line react/jsx-key
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrapper>
      </div>
    </div>
  );
}
